import { Card, IconButton } from "@material-ui/core";
import { observer } from "mobx-react";
import * as React from "react";
import { THEME } from "../../../theme/ThemeStore";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { generalStore } from "../../../stores/GeneralStore";
import { hrStore } from "../../../stores/HrStore";
import { IGetApplicantDetailsResponse } from "../../../types/models";
import { VerificationLine, VerificationSectionHeader } from "../../application/VerificationForm";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { SectionHeaderSmall } from "../../ui/Primitives";
import { getDataEntries } from "../../util/helpers";
import { ApplicantDetailsEditDialog } from "../dialogs/ApplicantDetailsEditDialog";

export const ApplicantDetailsSite = observer(() => {
    const isMobile = useMobileMediaQuery();
    const [openEditDialog, setOpenEditDialog] = React.useState(false);

    const { personalDataEntries, detailsEntries } = getDataEntries(hrStore.currentApplicant, "applicantDetails");

    const handleCloseEditApplicantDialog = () => {
        setOpenEditDialog(false);
    };

    const handleSubmitEditApplicantDialog = async (model: IGetApplicantDetailsResponse) => {
        try {
            generalStore.isLoading = true;
            await API.patchApplicant(hrStore.currentApplicantID, {
                firstName: model.firstName,
                lastName: model.lastName,
                jobCategory: model.jobCategory,
                phone: model.phone,
                email: model.email,
                previousProjects: model.previousProjects,
                discovery: model.discovery,
                personnelNumber: model.personnelNumber,
                shirtSize: model.shirtSize,
                salary: model.salary !== "" ? model.salary : undefined,
                experience: model.experience,
                startDate: model.startDate,
                noticePeriod: model.noticePeriod !== "" ? model.noticePeriod : undefined,
                workHours: model.workHours !== "" ? model.workHours : undefined,
                language: model.language,
                device: model.device,
                deviceSN: model.deviceSN,
                customDiscoverySource: model.customDiscoverySource
            });
            setOpenEditDialog(false);
        } catch (error) {
            generalStore.setError(t("error.updateApplicant"));
        } finally {
            hrStore.loadApplicant();
            generalStore.isLoading = false;
        }
    };

    return (
        <Card
            style={{
                display: "flex",
                padding: "25px 35px",
                flexGrow: 1,
                marginBottom: 55,
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 32 : 100
            }}
        >
            <div
                style={{
                    flex: 1,
                    marginBottom: isMobile ? 20 : 0,
                    color: THEME.customColors.headingsDark,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                }}
                className="body1"
            >
                <SectionHeaderSmall label={t("screen.verify.motivation.title")} style={{ marginBottom: 20 }} />
                {hrStore.currentApplicant.motivation}
            </div>

            <div style={{ flex: 1, overflow: "auto", position: "relative", display: "flex", flexDirection: "column" }}>
                {!isMobile && (
                    <div
                        style={{
                            position: "sticky",
                            top: 0,
                            marginRight: 10,
                            textAlign: "end",
                            zIndex: 2
                        }}
                    >
                        <IconButton
                            style={{
                                position: "static"
                            }}
                            disableRipple
                            onClick={() => setOpenEditDialog(true)}
                        >
                            <img src={THEME.Images.icEdit} alt="" />
                        </IconButton>
                    </div>
                )}

                <div style={isMobile ? {} : { width: "100%", height: 0, position: "absolute", top: 0 }}>
                    {isMobile ? (
                        <VerificationSectionHeader
                            label={t("screen.personalData.title")}
                            onClick={() => setOpenEditDialog(true)}
                            style={{ marginBottom: 20 }}
                        />
                    ) : (
                        <SectionHeaderSmall label={t("screen.personalData.title")} />
                    )}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            margin: isMobile ? "0 0 20px" : "10px 20px 40px 0"
                        }}
                    >
                        {personalDataEntries.map((entry, index) => (
                            <VerificationLine key={index} label={entry[0]} value={entry[1]} />
                        ))}
                    </div>
                    <SectionHeaderSmall label={t("screen.details.title")} style={{ marginBottom: 20 }} />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            margin: isMobile ? "0 0 20px" : "10px 20px 40px 0"
                        }}
                    >
                        {detailsEntries.map((entry, index) => (
                            <VerificationLine key={index} label={entry[0]} value={entry[1]} />
                        ))}
                    </div>
                </div>
            </div>
            <ApplicantDetailsEditDialog
                initialValues={hrStore.currentApplicant}
                isOpen={openEditDialog}
                onBack={handleCloseEditApplicantDialog}
                onSubmit={handleSubmitEditApplicantDialog}
            />
        </Card>
    );
});
